import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { 
  Phone, 
  MessageCircle, 
  ArrowLeft, 
  CheckCircle, 
  Clock, 
  Users, 
  FileCheck,
  Languages,
  HardHat,
  Cog,
  Flame,
  FlaskConical,
  UserPlus,
  Building2,
  Sparkles,
  ChevronLeft,
  MessageSquare,
  ClipboardList,
  Rocket,
  ArrowRight
} from "lucide-react";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  HardHat,
  Cog,
  FlaskConical,
  Flame,
  UserPlus,
  Building2,
  MessageSquare,
  ClipboardList,
  Rocket
};

export default function Trainings() {
  const { t, i18n } = useTranslation('trainings');
  const isRTL = i18n.language === 'he';
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;
  
  const { ref: introRef, isVisible: introVisible } = useScrollAnimation();
  const { ref: categoriesRef, isVisible: categoriesVisible } = useScrollAnimation();
  const { ref: processRef, isVisible: processVisible } = useScrollAnimation();
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();

  const trainingCategories = t('categories', { returnObjects: true }) as Array<{
    id: string;
    icon?: string;
    title: string;
    description: string;
    audience: string;
    deliverables: string[];
    duration: string;
    languages: string[];
    color?: string;
  }>;

  // Map category IDs to their dedicated page routes
  const categoryRoutes: Record<string, string> = {
    height: '/training/work-height',
    fire: '/training/fire-safety',
    process: '/training/hazmat',
  };

  const processSteps = t('processSteps', { returnObjects: true }) as Array<{
    number: string;
    icon?: string;
    title: string;
    description: string;
  }>;
  
  // Fallback to empty array if translation returns string
  const safeProcessSteps = Array.isArray(processSteps) ? processSteps : [];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary-light" />
        <div className="absolute inset-0 opacity-50" style={{ background: 'var(--gradient-mesh)' }} />
        <div className="absolute top-1/4 right-[10%] w-[400px] h-[400px] rounded-full bg-accent/15 blur-[100px] animate-float" />
        <div className="absolute inset-0 pattern-hero" />

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 backdrop-blur-sm mb-6 animate-fade-up">
              <HardHat className="w-4 h-4 text-accent" />
              <span className="text-primary-foreground/90 text-sm font-semibold">{t('hero.badge')}</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6 animate-fade-up" style={{ animationDelay: '0.1s' }}>
              {t('hero.title')}
              <br />
              <span className="text-gradient-accent">{t('hero.titleHighlight')}</span>
            </h1>
            <p className="text-lg md:text-xl text-primary-foreground/75 leading-relaxed animate-fade-up" style={{ animationDelay: '0.2s' }}>
              {t('hero.subtitle')}
            </p>
          </div>
        </div>

        {/* Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
            <path d="M0 80L60 72C120 64 240 48 360 40C480 32 600 32 720 36C840 40 960 48 1080 52C1200 56 1320 56 1380 56L1440 56V80H0Z" fill="hsl(var(--background))" />
          </svg>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-8">
          <div
            ref={introRef}
            className={`max-w-4xl mx-auto text-center animate-on-scroll ${introVisible ? 'is-visible' : ''}`}
          >
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: t('intro.text') }} />
          </div>
        </div>
      </section>

      {/* Training Categories */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8">
          <div
            ref={categoriesRef}
            className={`text-center max-w-3xl mx-auto mb-16 animate-on-scroll ${categoriesVisible ? 'is-visible' : ''}`}
          >
            <span className="inline-block text-accent font-bold text-sm uppercase tracking-widest mb-4 px-4 py-2 rounded-full bg-accent/10 border border-accent/20">
              {t('categoriesSection.badge')}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              {t('categoriesSection.title')}
            </h2>
            <p className="text-muted-foreground text-lg">
              {t('categoriesSection.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {trainingCategories.map((category, index) => {
              const IconComponent = iconMap[category.icon] || HardHat;
              return (
                <div
                  key={category.id}
                  className="group bg-card rounded-3xl p-8 border border-border shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-400 relative overflow-hidden"
                >
                  {/* Gradient accent */}
                  <div className={`absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl ${category.color} rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                  
                  <div className="relative">
                    {/* Header */}
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-accent/15 to-accent/5 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <IconComponent className="w-7 h-7 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-heading text-xl lg:text-2xl font-bold text-foreground mb-2 group-hover:text-accent transition-colors duration-300">
                          {category.title}
                        </h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          {category.description}
                        </p>
                      </div>
                    </div>

                    {/* Details Grid */}
                    <div className="space-y-4">
                      {/* Audience */}
                      <div className="flex items-start gap-3">
                        <Users className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{t('labels.audience')}</div>
                          <div className="text-sm text-foreground">{category.audience}</div>
                        </div>
                      </div>

                      {/* Deliverables */}
                      <div className="flex items-start gap-3">
                        <FileCheck className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('labels.deliverables')}</div>
                          <div className="flex flex-wrap gap-2">
                            {category.deliverables.map((item, i) => (
                              <span key={i} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md bg-secondary text-secondary-foreground">
                                <CheckCircle className="w-3 h-3 text-accent" />
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Duration & Languages */}
                      <div className="flex flex-wrap gap-6 pt-2">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-accent" />
                          <span className="text-sm text-foreground">{category.duration}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Languages className="w-4 h-4 text-accent" />
                          <span className="text-sm text-foreground">{category.languages.join(" / ")}</span>
                        </div>
                      </div>
                    </div>

                    {/* Link to dedicated page if exists */}
                    {categoryRoutes[category.id] && (
                      <div className="mt-6 pt-4 border-t border-border">
                        <Link
                          to={categoryRoutes[category.id]}
                          className="inline-flex items-center gap-2 text-accent font-semibold text-sm hover:gap-3 transition-all duration-200"
                        >
                          {isRTL ? 'לדף ההדרכה המלא' : 'View full training page'}
                          <ArrowIcon className="w-4 h-4" />
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 section-alt">
        <div className="container mx-auto px-4 md:px-8">
          <div
            ref={processRef}
            className={`text-center max-w-3xl mx-auto mb-16 animate-on-scroll ${processVisible ? 'is-visible' : ''}`}
          >
            <span className="inline-block text-accent font-bold text-sm uppercase tracking-widest mb-4 px-4 py-2 rounded-full bg-accent/10 border border-accent/20">
              {t('processSection.badge')}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              {t('processSection.title')}
            </h2>
            <p className="text-muted-foreground text-lg">
              {t('processSection.description')}
            </p>
          </div>

          {/* Steps */}
          <div className="relative max-w-4xl mx-auto">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-1/2 left-1/4 right-1/4 h-[2px] -translate-y-1/2">
              <div className="h-full bg-gradient-to-l from-border via-accent/30 to-border rounded-full" />
            </div>

            <div className={`grid grid-cols-1 md:grid-cols-3 gap-8 animate-on-scroll ${processVisible ? 'is-visible' : ''}`}>
              {safeProcessSteps.map((step, index) => {
                const StepIcon = step.icon ? iconMap[step.icon] : MessageSquare;
                return (
                  <div key={index} className="relative text-center group">
                    <div className="relative z-10">
                      {/* Icon */}
                      <div className="relative mx-auto mb-6">
                        <div className="w-24 h-24 mx-auto rounded-2xl bg-card border border-border flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-105 transition-all duration-500">
                          <StepIcon className="w-10 h-10 text-accent" />
                        </div>
                        <div className="absolute -top-2 -right-2 w-9 h-9 rounded-lg bg-gradient-to-br from-accent to-accent-dark flex items-center justify-center text-accent-foreground text-sm font-bold shadow-lg">
                          {step.number}
                        </div>
                      </div>

                      {/* Content */}
                      <h3 className="font-heading text-xl font-bold text-foreground mb-3 group-hover:text-accent transition-colors duration-300">
                        {step.title}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed max-w-xs mx-auto">
                        {step.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary-light" />
        <div className="absolute inset-0 opacity-50" style={{ background: 'var(--gradient-mesh)' }} />
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-accent/20 rounded-full blur-[120px] animate-float" />
        <div className="absolute inset-0 pattern-hero" />

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div
            ref={ctaRef}
            className={`max-w-3xl mx-auto text-center animate-on-scroll ${ctaVisible ? 'is-visible' : ''}`}
          >
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 backdrop-blur-sm mb-8">
              <Sparkles className="w-4 h-4 text-accent" />
              <span className="text-primary-foreground/90 text-sm font-semibold">{t('cta.badge')}</span>
            </div>

            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary-foreground mb-6 leading-tight">
              {t('cta.title')}
            </h2>

            <p className="text-lg md:text-xl text-primary-foreground/75 mb-10 leading-relaxed">
              {t('cta.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+972528300088">
                <Button variant="cta" size="lg" className="gap-2 w-full sm:w-auto text-base font-semibold">
                  <Phone className="w-5 h-5" />
                  {t('cta.phone')}
                </Button>
              </a>
              <a
                href="https://wa.me/972528300088?text=שלום,%20אשמח%20לשמוע%20על%20הדרכות%20בטיחות"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" size="lg" className="gap-2 w-full sm:w-auto text-base font-semibold bg-transparent border-2 border-primary-foreground/60 text-primary-foreground hover:bg-primary-foreground/10 hover:border-primary-foreground">
                  <MessageCircle className="w-5 h-5" />
                  {t('cta.whatsapp')}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
